import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenSupportCase } from '../hooks/useSupportCases';
import type { SupportCaseReason } from '../types/supportCase';

const REASONS: ReadonlyArray<SupportCaseReason> = [
  'EMAIL_NOT_RECEIVED',
  'TOKEN_EXPIRED',
  'OFFLINE_PAYMENT_DEPOSIT',
  'OFFLINE_PAYMENT_FULL',
  'OTHER',
];

interface Props {
  bookingId: string;
  onSuccess: (caseId: string) => void;
  onError: (message: string) => void;
  onCancel?: () => void;
}

export const OpenSupportCaseForm = ({ bookingId, onSuccess, onError, onCancel }: Props) => {
  const { t } = useTranslation(['forms', 'badges', 'errors']);
  const [reason, setReason] = useState<SupportCaseReason | ''>('');
  const [note, setNote] = useState('');
  const [reasonError, setReasonError] = useState('');

  const { mutate: openCase, isPending } = useOpenSupportCase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason) {
      setReasonError(t('forms:reasonRequired', 'Reason is required'));
      return;
    }

    setReasonError('');

    openCase(
      { bookingId, reason, note: note.trim() || undefined },
      {
        onSuccess: (sc) => onSuccess(sc.caseId),
        onError: (err: unknown) => {
          const anyErr = err as { response?: { status?: number; data?: { detail?: string } } };
          const status = anyErr?.response?.status;
          if (status === 404) {
            onError(t('errors:supportCaseNotFoundOrForbidden', 'Booking not found or not accessible.'));
          } else if (status === 422) {
            onError(anyErr?.response?.data?.detail ?? t('errors:supportCaseTerminal', 'Booking is terminal and cannot have a support case.'));
          } else {
            onError(t('errors:unexpectedError', 'An unexpected error occurred.'));
          }
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label htmlFor="support-reason" className="block text-sm font-medium text-gray-700 mb-1">
          {t('forms:caseReason', 'Reason')} <span aria-hidden="true">*</span>
        </label>
        <select
          id="support-reason"
          value={reason}
          onChange={e => setReason(e.target.value as SupportCaseReason)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-describedby={reasonError ? 'reason-error' : undefined}
        >
          <option value="">{t('forms:selectReason', 'Select a reason...')}</option>
          {REASONS.map(r => (
            <option key={r} value={r}>
              {t(`badges:supportCaseReason${r.replace(/_./g, m => m[1].toUpperCase())}` as never, r)}
            </option>
          ))}
        </select>
        {reasonError && (
          <p id="reason-error" className="mt-1 text-xs text-red-600" role="alert">
            {reasonError}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="support-note" className="block text-sm font-medium text-gray-700 mb-1">
          {t('forms:caseNote', 'Note')} ({t('forms:optional', 'optional')}, {t('forms:maxChars', 'max {{n}} chars', { n: 500 })})
        </label>
        <textarea
          id="support-note"
          value={note}
          onChange={e => setNote(e.target.value)}
          maxLength={500}
          rows={3}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={t('forms:caseNotePlaceholder', 'Additional context (max 500 characters)...')}
        />
        <p className="text-xs text-gray-400 text-right">{note.length}/500</p>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isPending ? t('forms:submitting', 'Submitting...') : t('forms:openCase', 'Open Case')}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('forms:cancel', 'Cancel')}
          </button>
        )}
      </div>
    </form>
  );
};
